import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["scrollDirection"];
import { useMemo } from 'react';
import styled, { css } from 'styled-components';
import I18n from 'I18n';
import { GREAT_WHITE, GYPSUM, OLAF, OZ } from 'HubStyleTokens/colors';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIBadge from 'UIComponents/badge/UIBadge';
import UIFlex from 'UIComponents/layout/UIFlex';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIImage from 'UIComponents/image/UIImage';
import UILink from 'UIComponents/link/UILink';
import UIScrollContainer from 'UIComponents/scroll/UIScrollContainer';
import UITable from 'UIComponents/table/UITable';
import { ProductLinePageIcons, CRM as CRM_ICON } from 'ui-addon-upgrades/_core/productPage/ProductPageIcons';
import * as ProductLines from 'self-service-api/constants/ProductLines';
import * as Tier from '../../constants/groupTags/Tiers';
import * as TierGroups from '../../constants/groupTags/TierGroups';
import { FLATTENED_TIER_ORDER } from '../constants';
import { getPricingUrl } from '../../utils/urls';
import { getTierI18nKey, getTierHubI18nKey, getTierLevelI18nKey } from '../../utils/i18n';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const TierGroupIcons = {
  CRM: CRM_ICON,
  MARKETING: ProductLinePageIcons[ProductLines.MARKETING],
  SALES: ProductLinePageIcons[ProductLines.SALES],
  SERVICE: ProductLinePageIcons[ProductLines.SERVICE],
  CMS: ProductLinePageIcons[ProductLines.CMS],
  CONTENT: ProductLinePageIcons[ProductLines.CMS],
  OPERATIONS: ProductLinePageIcons[ProductLines.OPERATIONS]
};
const borderStyles = css(["border-left:", ";"], props => props.showBorder ? `1px solid ${GREAT_WHITE} !important` : 0);
const TR = styled.tr.withConfig({
  displayName: "FeatureCompatibilityMatrix__TR",
  componentId: "gbayuu-0"
})(["background-color:", " !important;&:hover{background-color:", " !important;}"], props => props.index % 2 === 0 ? GYPSUM : OLAF, props => props.index % 2 === 0 ? GYPSUM : OLAF);
TR.displayName = 'tr';
const TH = styled.th.withConfig({
  displayName: "FeatureCompatibilityMatrix__TH",
  componentId: "gbayuu-1"
})(["", ";text-transform:uppercase;font-weight:600;font-size:12px;padding:12px;"], borderStyles);
const TD = styled.td.withConfig({
  displayName: "FeatureCompatibilityMatrix__TD",
  componentId: "gbayuu-2"
})(["", ";height:", ";"], borderStyles, props => props.compact ? '48px !important' : 'inherit');
TD.displayName = 'td';
const ScrollContainer = _ref => {
  let {
      scrollDirection
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  return /*#__PURE__*/_jsx(UIScrollContainer, Object.assign({}, rest, {
    size: 40,
    style: {
      height: scrollDirection === 'both' ? '100%' : 'initial'
    }
  }));
};
export default function FeatureCompatibilityMatrix({
  features,
  featureType,
  isCustomer,
  portalTiers,
  tierGroupNames,
  trackPricingLinkClick
}) {
  const renderedHubsHeader = /*#__PURE__*/_jsxs(TR, {
    index: 0,
    style: {
      whiteSpace: 'nowrap',
      fontWeight: 600
    },
    children: [/*#__PURE__*/_jsx(TD, {}), tierGroupNames.map(tierGroup => /*#__PURE__*/_jsx(TD, {
      colSpan: TierGroups[tierGroup].length,
      children: /*#__PURE__*/_jsxs(UIFlex, {
        align: "center",
        justify: "center",
        children: [/*#__PURE__*/_jsx(UIImage, {
          className: "display-inline-block m-right-2",
          src: TierGroupIcons[tierGroup],
          width: 16
        }), /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierHubI18nKey(tierGroup)
        })]
      })
    }, tierGroup))]
  });
  const renderedHubLevels = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: 1,
    compact: true,
    style: {
      whiteSpace: 'nowrap',
      height: 48
    },
    children: [/*#__PURE__*/_jsx(TH, {
      className: "p-left-7",
      style: {
        backgroundColor: 'inherit',
        boxShadow: 'inset -3px 0 3px -3px #cbd6e2',
        left: '0',
        position: 'sticky',
        textAlign: 'start'
      },
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'marketplace-ui-common.compatibility.featureCompatibilityMatrix.features',
        options: {
          type: featureType
        }
      })
    }), tierGroupNames.map(tierGroup => TierGroups[tierGroup].map((tier, index) => {
      const tierLevel = tier.split('_')[1];
      const isUserPlan = portalTiers.length ? portalTiers.includes(tier) : tier === Tier.HUBSPOT_FREE;
      return /*#__PURE__*/_jsxs(TH, {
        showBorder: index === 0,
        "aria-label": I18n.text(getTierI18nKey(tier)),
        children: [isUserPlan && isCustomer ? /*#__PURE__*/_jsx("div", {
          style: {
            marginTop: -30,
            marginBottom: 10
          },
          children: /*#__PURE__*/_jsx(UIBadge, {
            use: "default",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `marketplace-ui-common.compatibility.featureCompatibilityMatrix.yourPlan`
            })
          })
        }) : null, /*#__PURE__*/_jsx(FormattedMessage, {
          message: getTierLevelI18nKey(tierLevel)
        })]
      }, tier);
    }))]
  }), [featureType, isCustomer, portalTiers, tierGroupNames]);
  const renderedFeatureRows = useMemo(() => features.map((feature, featureIndex) => /*#__PURE__*/_jsxs(TR, {
    index: featureIndex,
    children: [/*#__PURE__*/_jsx(TD, {
      className: "p-left-7",
      style: {
        backgroundColor: 'inherit',
        boxShadow: 'inset -3px 0 3px -3px #cbd6e2',
        left: '0',
        position: 'sticky',
        textAlign: 'start'
      },
      children: feature.text
    }), tierGroupNames.map(tierGroup => TierGroups[tierGroup].map((tier, index) => {
      const isFeatureCompatible = feature.tiers.includes(tier);
      return /*#__PURE__*/_jsx(TD, {
        showBorder: index === 0,
        children: isFeatureCompatible ? /*#__PURE__*/_jsx(UIIcon, {
          "data-test-id": `${tier}-feature-${featureIndex}-check`,
          name: "success",
          color: OZ
        }) : null
      }, `${tier}-feature-${featureIndex}`);
    }))]
  }, `feature-row-${featureIndex}`)), [features, tierGroupNames]);
  const renderedFooter = useMemo(() => /*#__PURE__*/_jsxs(TR, {
    index: features.length,
    children: [/*#__PURE__*/_jsx(TD, {
      compact: true
    }), tierGroupNames.map(tierGroup => /*#__PURE__*/_jsx(TD, {
      colSpan: TierGroups[tierGroup].length,
      compact: true,
      showBorder: true,
      style: {
        fontSize: 12,
        whiteSpace: 'nowrap'
      },
      children: /*#__PURE__*/_jsx(UILink, {
        "data-test-id": `${tierGroup}-pricing-link`,
        external: true,
        href: getPricingUrl(!isCustomer, tierGroup),
        onClick: () => {
          if (trackPricingLinkClick) {
            trackPricingLinkClick(tierGroup);
          }
        },
        children: tierGroup === 'CRM' ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-common.compatibility.featureCompatibilityMatrix.seeFeatures'
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'marketplace-ui-common.compatibility.featureCompatibilityMatrix.seePricingAndFeatures'
        })
      })
    }, tierGroup))]
  }), [features, isCustomer, tierGroupNames, trackPricingLinkClick]);
  return /*#__PURE__*/_jsxs(UITable, {
    ScrollContainer: ScrollContainer,
    style: {
      textAlign: 'center'
    },
    children: [/*#__PURE__*/_jsxs("colgroup", {
      children: [/*#__PURE__*/_jsx("col", {
        style: {
          minWidth: 250
        }
      }), /*#__PURE__*/_jsx("col", {
        span: FLATTENED_TIER_ORDER.length
      })]
    }), /*#__PURE__*/_jsxs("tbody", {
      children: [renderedHubsHeader, renderedHubLevels, renderedFeatureRows, renderedFooter]
    })]
  });
}